// src/pages/UploadVerses.js

import React, { useState } from 'react';
import Layout from './Layout';
import { collection, setDoc, doc, increment, updateDoc, getDoc, getDocs, Timestamp, query, where, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Papa from 'papaparse';
import { Link } from 'react-router-dom';
import { FiDownload, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const GROWTH_AREAS = [
  'Spiritual Development',
  'Holisitic Personal Growth',
  'Relational Development',
  'Physical Development',
  'Vocational Development',
  'Missional Development',
  'Emotional Development'
];

const DEVELOPMENT_TOPICS = [
  'Christian Character and Virtues',
  'Cultrual Engagement and Worldview',
  'Dealing with Trials and Suffering',
  'Discipeship and Mentoring Others',
  'Evangelism and Sharing Your Faith',
  'Finances - Handling Money Gods Way',
  'Identity in Christ and Intrinsic Value',
  'Leadership and Servanthood',
  'Marriage and Family',
  'Mental and Emotional Health',
  'Moral and Ethical Decision-Making',
  'Overcoming Temptation and Sin',
  'Personal Faith and Relationship with God',
  'Prayer and Spiritual Disciplines',
  'Relationships and Community',
  'Sexual Purity and Enjoyment',
  'Spiritual Warfare',
  'Stewardship of the Body',
  'Time Management and Priorities',
  'Understanding the Bible',
  'Work and Stewardship'
];

const calculateInitialDifficulty = (verse) => {
  let difficulty = 1;
  
  // Length-based difficulty
  if (verse.text) {
    const wordCount = verse.text.split(' ').length;
    if (wordCount > 50) difficulty++;
    if (wordCount > 100) difficulty++;
  }

  // Concept-based difficulty
  const advancedConcepts = [
    'theology', 'doctrine', 'sovereignty', 'predestination', 
    'trinity', 'sanctification', 'justification'
  ];
  
  if (verse.concept) {
    const hasAdvancedConcept = advancedConcepts.some(
      concept => verse.concept.toLowerCase().includes(concept)
    );
    if (hasAdvancedConcept) difficulty++;
  }

  // Growth area based difficulty
  const advancedAreas = ['Spiritual Development', 'Missional Development'];
  if (advancedAreas.includes(verse.growthArea)) {
    difficulty++;
  }

  return Math.min(difficulty, 5);
};

// Add state for new enrichment fields
const AGE_RELEVANCE_KEYS = [
  'elementary', 'middleSchool', 'highSchool', 'youngAdult', 'middleAge', 'wisdomYears'
];
const RELATIONSHIP_RELEVANCE_KEYS = [
  'youth', 'singleYoungAdult', 'singleAdult', 'dating', 'engaged', 'earlyMarriage', 'experiencedMarriage', 'marriedWithYoungKids', 'marriedWithOlderKids', 'grandparents', 'separated', 'divorced', 'widowed'
];
const DISCIPLESHIP_STAGE_KEYS = [
  'notCurrentlyBeliever', 'newBeliever', 'growingDisciple', 'discipleMaker', 'sageStage'
];
const CHRISTIAN_EXPERIENCE_KEYS = [
  'noExperienceYet', 'newToFaith', 'grewUpStillWalking', 'cameToFaithNowWalking', 'crisisLeaningIn', 'crisisLeaningOut', 'walkedAwayCurrently', 'walkedAwayNowOpen', 'walkedAwayNowReturned'
];

// Book abbreviation mapping (same as AppAdmin.js)
const bookAbbreviations = {
  "Gen.": "Genesis", "Ex.": "Exodus", "Lev.": "Leviticus", "Num.": "Numbers", "Deut.": "Deuteronomy", "Josh.": "Joshua", "Judg.": "Judges", "Ruth": "Ruth",
  "1 Sam.": "1 Samuel", "1 Samuel": "1 Samuel", "2 Sam.": "2 Samuel", "2 Samuel": "2 Samuel",
  "1 Kgs.": "1 Kings", "1 Kings": "1 Kings", "2 Kgs.": "2 Kings", "2 Kings": "2 Kings",
  "1 Chron.": "1 Chronicles", "1 Chronicles": "1 Chronicles", "2 Chron.": "2 Chronicles", "2 Chronicles": "2 Chronicles",
  "Ezra": "Ezra", "Neh.": "Nehemiah", "Nehemiah": "Nehemiah", "Esth.": "Esther", "Esther": "Esther",
  "Job": "Job", "Ps.": "Psalms", "Psalm": "Psalms", "Psalms": "Psalms", "Prov.": "Proverbs", "Proverbs": "Proverbs",
  "Eccl.": "Ecclesiastes", "Ecclesiastes": "Ecclesiastes", "Song": "Song of Solomon", "Song of Solomon": "Song of Solomon",
  "Isa.": "Isaiah", "Isaiah": "Isaiah", "Jer.": "Jeremiah", "Jeremiah": "Jeremiah", "Lam.": "Lamentations", "Lamentations": "Lamentations",
  "Ezek.": "Ezekiel", "Ezekiel": "Ezekiel", "Dan.": "Daniel", "Daniel": "Daniel", "Hos.": "Hosea", "Hosea": "Hosea",
  "Joel": "Joel", "Amos": "Amos", "Obad.": "Obadiah", "Obadiah": "Obadiah", "Jonah": "Jonah", "Mic.": "Micah", "Micah": "Micah",
  "Nah.": "Nahum", "Nahum": "Nahum", "Hab.": "Habakkuk", "Habakkuk": "Habakkuk", "Zeph.": "Zephaniah", "Zephaniah": "Zephaniah",
  "Hag.": "Haggai", "Haggai": "Haggai", "Zech.": "Zechariah", "Zechariah": "Zechariah", "Mal.": "Malachi", "Malachi": "Malachi",
  // New Testament
  "Matt.": "Matthew", "Matthew": "Matthew", "Mark": "Mark", "Luke": "Luke", "John": "John", "Acts": "Acts",
  "Rom.": "Romans", "Romans": "Romans", "1 Cor.": "1 Corinthians", "1 Corinthians": "1 Corinthians", "2 Cor.": "2 Corinthians", "2 Corinthians": "2 Corinthians",
  "Gal.": "Galatians", "Galatians": "Galatians", "Eph.": "Ephesians", "Ephesians": "Ephesians", "Phil.": "Philippians", "Philippians": "Philippians",
  "Col.": "Colossians", "Colossians": "Colossians",
  "1 Thess.": "1 Thessalonians", "1 Thes.": "1 Thessalonians", "1 Thessalonians": "1 Thessalonians",
  "2 Thess.": "2 Thessalonians", "2 Thes.": "2 Thessalonians", "2 Thessalonians": "2 Thessalonians",
  "1 Tim.": "1 Timothy", "1 Timothy": "1 Timothy", "2 Tim.": "2 Timothy", "2 Timothy": "2 Timothy",
  "Titus": "Titus", "Philem.": "Philemon", "Philemon": "Philemon", "Heb.": "Hebrews", "Hebrews": "Hebrews",
  "James": "James", "1 Pet.": "1 Peter", "1 Peter": "1 Peter", "2 Pet.": "2 Peter", "2 Peter": "2 Peter",
  "1 John": "1 John", "2 John": "2 John", "3 John": "3 John", "Jude": "Jude", "Rev.": "Revelation", "Revelation": "Revelation"
};

function normalizeReference(ref) {
  if (!ref) return ref;
  const match = ref.match(/^((?:\d ?)?[A-Za-z. ]+?) ?(\d+:\d+(?:,\d+)?)/);
  if (match) {
    let book = match[1].trim();
    let rest = match[2];
    if (book === 'Psalm') book = 'Psalms';
    if (bookAbbreviations[book]) {
      return `${bookAbbreviations[book]} ${rest}`;
    }
  }
  return ref;
}

const UploadVerses = () => {
  const [file, setFile] = useState(null);
  const [verse, setVerse] = useState('');
  const [reference, setReference] = useState('');
  const [url, setUrl] = useState('');
  const [categories, setCategories] = useState('');
  const [tags, setTags] = useState('');
  const [question, setQuestion] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [growthArea, setGrowthArea] = useState('');
  const [devTopic, setDevTopic] = useState('');
  const [concept, setConcept] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState(1);
  const [relatedVerses, setRelatedVerses] = useState('');
  const [contextualMeaning, setContextualMeaning] = useState('');
  const [themeMetadata, setThemeMetadata] = useState({
    emotional: 0,
    spiritual: 0,
    relational: 0,
    vocational: 0,
    physical: 0,
    missional: 0
  });
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState({
    show: false,
    message: '',
    details: null
  });

  // Add state for new enrichment fields
  const [ageRelevance, setAgeRelevance] = useState(() => Object.fromEntries(AGE_RELEVANCE_KEYS.map(k => [k, 0])));
  const [relationshipRelevance, setRelationshipRelevance] = useState(() => Object.fromEntries(RELATIONSHIP_RELEVANCE_KEYS.map(k => [k, 0])));
  const [discipleshipStageRelevance, setDiscipleshipStageRelevance] = useState(() => Object.fromEntries(DISCIPLESHIP_STAGE_KEYS.map(k => [k, 0])));
  const [christianExperienceRelevance, setChristianExperienceRelevance] = useState(() => Object.fromEntries(CHRISTIAN_EXPERIENCE_KEYS.map(k => [k, 0])));

  const [normalizeConfirm, setNormalizeConfirm] = useState({ show: false, changes: [] });

  // Move checkDuplicates to be a reusable function at component level
  const checkDuplicates = async (reference) => {
    const versesRef = collection(db, 'verses');
    const q = query(versesRef, where('reference', '==', reference));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleGroupSliderChange = (groupSetter, groupState) => (key) => (e) => {
    groupSetter({ ...groupState, [key]: parseFloat(e.target.value) });
  };

  const handleSingleUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Check for duplicates first
      if (await checkDuplicates(reference)) {
        setUploadMessage(`Verse with reference "${reference}" already exists in the database.`);
        setIsLoading(false);
        return;
      }

      const normalizedRef = normalizeReference(reference);
      const normalized = normalizedRef !== reference;
      const verseData = {
        text: verse,
        reference: normalizedRef,
        url,
        question,
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        categories: categories.split(',').map(cat => cat.trim()).filter(cat => cat),
        growthArea,
        devTopic,
        concept,
        difficultyLevel: Number(difficultyLevel),
        relatedVerses: relatedVerses.split(',').map(ref => ref.trim()).filter(ref => ref),
        contextualMeaning,
        themeMetadata: {
          emotional: parseFloat(themeMetadata.emotional),
          spiritual: parseFloat(themeMetadata.spiritual),
          relational: parseFloat(themeMetadata.relational),
          vocational: parseFloat(themeMetadata.vocational),
          physical: parseFloat(themeMetadata.physical),
          missional: parseFloat(themeMetadata.missional)
        },
        ageRelevance,
        relationshipRelevance,
        discipleshipStageRelevance,
        christianExperienceRelevance,
        lastUpdated: new Date().toISOString()
      };
      
      const counterRef = doc(db, 'counters', 'verseCounter');
      const counterSnap = await getDoc(counterRef);
      let newVerseId;
      if (!counterSnap.exists()) {
        await setDoc(counterRef, { count: 1 });
        newVerseId = 'Ver001';
      } else {
        const currentCount = counterSnap.data().count;
        newVerseId = `Ver${(currentCount + 1).toString().padStart(3, '0')}`;
        await updateDoc(counterRef, { count: increment(1) });
      }

      await setDoc(doc(db, 'verses', newVerseId), verseData);
      setUploadMessage("Verse uploaded successfully!");
      
      // Reset all form fields
      setVerse('');
      setReference('');
      setUrl('');
      setQuestion('');
      setTags('');
      setCategories('');
      setGrowthArea('');
      setDevTopic('');
      setConcept('');
      setDifficultyLevel(1);
      setRelatedVerses('');
      setContextualMeaning('');
      setThemeMetadata({
        emotional: 0,
        spiritual: 0,
        relational: 0,
        vocational: 0,
        physical: 0,
        missional: 0
      });
      setAgeRelevance(() => Object.fromEntries(AGE_RELEVANCE_KEYS.map(k => [k, 0])));
      setRelationshipRelevance(() => Object.fromEntries(RELATIONSHIP_RELEVANCE_KEYS.map(k => [k, 0])));
      setDiscipleshipStageRelevance(() => Object.fromEntries(DISCIPLESHIP_STAGE_KEYS.map(k => [k, 0])));
      setChristianExperienceRelevance(() => Object.fromEntries(CHRISTIAN_EXPERIENCE_KEYS.map(k => [k, 0])));
      setIsLoading(false);

      if (normalized) {
        setNormalizeConfirm({ show: true, changes: [[reference, normalizedRef]] });
      }
    } catch (error) {
      console.error("Error adding verse:", error);
      setUploadMessage("Failed to upload the verse.");
      setIsLoading(false);
    }
  };
  

  const handleUpload = async () => {
    if (!file) return;
    setIsLoading(true);
    setUploadResult({ 
      show: true, 
      message: 'Starting upload process...', 
      details: `File: ${file.name}` 
    });

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const results = Papa.parse(text, { 
          header: true, 
          delimiter: '\t',
          skipEmptyLines: true
        });

        let successCount = 0;
        let duplicateCount = 0;
        let errors = [];
        let normalizationChanges = [];

        // Get the current counter value
        const counterRef = doc(db, 'counters', 'verseCounter');
        let counterDoc = await getDoc(counterRef);
        if (!counterDoc.exists()) {
          await setDoc(counterRef, { count: 0 });
          counterDoc = await getDoc(counterRef);
        }
        let currentCount = counterDoc.data().count || 0;

        // Process each row
        for (let i = 0; i < results.data.length; i++) {
          const row = results.data[i];
          // Accept either 'Verse Text' or 'Text' as the verse text column
          const verseText = row['Verse Text'] || row['Text'];
          if (!row.Reference || !verseText) {
            continue;
          }
          try {
            // Check for duplicates before processing
            if (await checkDuplicates(row.Reference)) {
              duplicateCount++;
              continue;
            }
            currentCount++;
            const verseId = `Ver${String(currentCount).padStart(3, '0')}`;
            const normalizedRef = normalizeReference(row.Reference);
            if (normalizedRef !== row.Reference) {
              normalizationChanges.push([row.Reference, normalizedRef]);
            }
            // Create verse document with only reference and text
            const verseData = {
              reference: normalizedRef,
              text: verseText,
              // All other fields left empty or default for enrichment
              lastUpdated: new Date(),
              verseId
            };
            await setDoc(doc(db, 'verses', verseId), verseData);
            successCount++;
          } catch (error) {
            errors.push(`Row ${i} (${row.Reference}): ${error.message}`);
          }
        }
        // Update the counter
        await setDoc(counterRef, { count: currentCount });
        setUploadResult({
          show: true,
          message: 'Upload complete',
          details: {
            successCount,
            duplicateCount,
            errors,
            lastVerseId: `Ver${String(currentCount).padStart(3, '0')}`
          }
        });

        if (normalizationChanges.length > 0) {
          setNormalizeConfirm({ show: true, changes: normalizationChanges });
        }
      } catch (error) {
        setUploadResult({
          show: true,
          message: 'Error during upload',
          details: error.message
        });
      } finally {
        setIsLoading(false);
      }
    };

    reader.onerror = (error) => {
      setUploadResult({
        show: true,
        message: 'Error reading file',
        details: JSON.stringify(error, null, 2)
      });
      setIsLoading(false);
    };

    try {
      reader.readAsText(file);
    } catch (error) {
      setUploadResult({
        show: true,
        message: 'Error starting file read',
        details: error.message
      });
      setIsLoading(false);
    }
  };

  // Excel Template Structure
  const excelStructure = {
    sheets: {
      'Instructions': {
        // Help text and guidelines
        A1: 'Bible Verse Upload Template Instructions',
        // ... detailed instructions
      },
      'Verse Upload Template': {
        // Headers with validation rules
        headers: [
          { name: 'Reference', required: true, validation: 'text' },
          { name: 'Verse Text', required: true, validation: 'text' },
          { name: 'Bible.com URL', required: false, validation: 'url' },
          // ... other headers
        ],
        // Example row (1 Timothy 4:8)
        exampleData: {
          // ... example data
        }
      },
      'Reference Lists': {
        // Validation data
        growthAreas: GROWTH_AREAS,
        developmentTopics: DEVELOPMENT_TOPICS
      }
    }
  }

  return (
    <div>
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Verse Uploading
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Add Bible verses to the system for mentors to share with their disciples.
              <Link to="/search-verses-system" className="text-blue-600 hover:text-blue-800 ml-2">
                View All Verses →
              </Link>
            </p>
          </div>

          {/* Loading Screen */}
          {isLoading && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-8 rounded-lg shadow-xl text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-700">{uploadMessage}</p>
                {duplicateCount > 0 && (
                  <p className="text-yellow-600 mt-2">{duplicateCount} duplicate(s) skipped</p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column - Single Verse Form */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-2 border-b">
                Add a Single Verse
              </h2>
              <form onSubmit={handleSingleUpload} className="space-y-6">
                {/* Reference */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Reference</label>
                  <input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="e.g., John 3:16"
                    required
                  />
                </div>

                {/* Verse Text */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Verse Text</label>
                  <textarea
                    value={verse}
                    onChange={(e) => setVerse(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    required
                  />
                </div>

                {/* Bible.com URL */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Bible.com URL
                    <span className="text-sm text-gray-500 ml-2">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="https://www.bible.com/..."
                  />
                </div>

                {/* Reflection Question */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Reflection Question
                    <span className="text-sm text-gray-500 ml-2">(optional)</span>
                  </label>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="A question to help apply this verse..."
                  />
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tags
                    <span className="text-sm text-gray-500 ml-2">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="faith, trust, prayer"
                  />
                </div>

                {/* Categories */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Categories
                    <span className="text-sm text-gray-500 ml-2">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={categories}
                    onChange={(e) => setCategories(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="Spiritual Growth, Prayer"
                  />
                </div>

                {/* Growth Area */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Personal Growth Area
                    <span className="text-sm text-gray-500 ml-2">(optional)</span>
                  </label>
                  <select
                    value={growthArea}
                    onChange={(e) => setGrowthArea(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                  >
                    <option value="">Select a growth area...</option>
                    {GROWTH_AREAS.map((area) => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                </div>

                {/* Development Topic */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Development Topic
                    <span className="text-sm text-gray-500 ml-2">(optional)</span>
                  </label>
                  <select
                    value={devTopic}
                    onChange={(e) => setDevTopic(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                  >
                    <option value="">Select a development topic...</option>
                    {DEVELOPMENT_TOPICS.map((topic) => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </select>
                </div>

                {/* Concept */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Concept
                    <span className="text-sm text-gray-500 ml-2">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="e.g., Casting Anxiety, God's Care"
                  />
                </div>

                {/* Difficulty Level */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Difficulty Level
                    <span className="text-sm text-gray-500 ml-2">(1-5)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={difficultyLevel}
                      onChange={(e) => setDifficultyLevel(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-gray-700 font-medium">{difficultyLevel}</span>
                  </div>
                </div>

                {/* Related Verses */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Related Verses
                    <span className="text-sm text-gray-500 ml-2">(comma-separated references)</span>
                  </label>
                  <input
                    type="text"
                    value={relatedVerses}
                    onChange={(e) => setRelatedVerses(e.target.value)}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="John 3:16, Romans 8:28"
                  />
                </div>

                {/* Contextual Meaning */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Contextual Meaning
                    <span className="text-sm text-gray-500 ml-2">(optional)</span>
                  </label>
                  <textarea
                    value={contextualMeaning}
                    onChange={(e) => setContextualMeaning(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border-2 border-gray-200 px-3 py-2"
                    placeholder="Describe how this verse applies to everyday situations..."
                  />
                </div>

                {/* Theme Analysis */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Theme Analysis</h3>
                  {Object.entries(themeMetadata).map(([theme, value]) => (
                    <div key={theme} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {theme} Impact
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={value}
                        onChange={e => setThemeMetadata(prev => ({
                          ...prev,
                          [theme]: parseFloat(e.target.value)
                        }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-gray-500">{value}</span>
                    </div>
                  ))}
                </div>

                {/* Age Relevance Sliders */}
                <div className="space-y-4 mt-8">
                  <h3 className="text-lg font-semibold text-gray-800">Age Relevance</h3>
                  {AGE_RELEVANCE_KEYS.map((key) => (
                    <div key={key} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={ageRelevance[key]}
                        onChange={handleGroupSliderChange(setAgeRelevance, ageRelevance)(key)}
                        className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-gray-500">{ageRelevance[key]}</span>
                    </div>
                  ))}
                </div>

                {/* Relationship Relevance Sliders */}
                <div className="space-y-4 mt-8">
                  <h3 className="text-lg font-semibold text-gray-800">Relationship Status Relevance</h3>
                  {RELATIONSHIP_RELEVANCE_KEYS.map((key) => (
                    <div key={key} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={relationshipRelevance[key]}
                        onChange={handleGroupSliderChange(setRelationshipRelevance, relationshipRelevance)(key)}
                        className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-gray-500">{relationshipRelevance[key]}</span>
                    </div>
                  ))}
                </div>

                {/* Discipleship Stage Relevance Sliders */}
                <div className="space-y-4 mt-8">
                  <h3 className="text-lg font-semibold text-gray-800">Discipleship Stage Relevance</h3>
                  {DISCIPLESHIP_STAGE_KEYS.map((key) => (
                    <div key={key} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={discipleshipStageRelevance[key]}
                        onChange={handleGroupSliderChange(setDiscipleshipStageRelevance, discipleshipStageRelevance)(key)}
                        className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-gray-500">{discipleshipStageRelevance[key]}</span>
                    </div>
                  ))}
                </div>

                {/* Christian Experience Relevance Sliders */}
                <div className="space-y-4 mt-8">
                  <h3 className="text-lg font-semibold text-gray-800">Christian Experience Relevance</h3>
                  {CHRISTIAN_EXPERIENCE_KEYS.map((key) => (
                    <div key={key} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={christianExperienceRelevance[key]}
                        onChange={handleGroupSliderChange(setChristianExperienceRelevance, christianExperienceRelevance)(key)}
                        className="w-full h-2 bg-pink-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-gray-500">{christianExperienceRelevance[key]}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Upload Verse
                </button>
              </form>
            </div>

            {/* Right Column - Bulk Upload */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Bulk Upload Verses
                </h2>
                <a
                  href="/templates/verse-upload-template.xlsx"
                  download
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                >
                  <FiDownload className="mr-2" />
                  Download Template
                </a>
              </div>
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">File Format Requirements:</h3>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Tab-delimited text file (.txt or .tsv)</li>
                    <li>First row must contain these exact column headers (case sensitive):</li>
                    <li>Reference | Verse Text</li>
                    <li>Only Reference and Verse Text are required fields</li>
                  </ul>
                </div>

                <div className="mt-4 border-t pt-4">
                  <button
                    onClick={() => setIsPromptOpen(!isPromptOpen)}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700 focus:outline-none"
                  >
                    <span className="mr-2">Perfect Prompt</span>
                    {isPromptOpen ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                  
                  {isPromptOpen && (
                    <div className="mt-2 p-4 bg-gray-50 rounded-md text-sm text-gray-700 whitespace-pre-wrap">
                      <div className="prose prose-sm max-w-none">
                        <h3 className="text-base font-medium mb-2">ChatGPT Prompt for Generating Verses</h3>
                        <pre className="bg-white p-4 rounded-md overflow-auto text-xs">
{`Generate 25 Bible verses focused on personal development and growth. For each verse, provide a complete structured entry without quotation marks, ensuring diverse coverage across different growth areas and development topics. Each entry should include:

1. Reference: Book Chapter:Verse format
2. Verse Text: Complete verse text without quotes
3. Bible.com URL: Full URL to NIV version
4. Reflection Question: One clear application question
5. Tags: 3-5 relevant comma-separated keywords
6. Categories: 2-3 comma-separated categories
7. Personal Growth Area: Exactly one of:
Spiritual Development
Holisitic Personal Growth
Relational Development
Physical Development
Vocational Development
Missional Development
Emotional Development

8. Development Topic: Exactly one of:
Christian Character and Virtues
Cultural Engagement and Worldview
Dealing with Trials and Suffering
Discipleship and Mentoring Others
Evangelism and Sharing Your Faith
Finances - Handling Money Gods Way
Identity in Christ and Intrinsic Value
Leadership and Servanthood
Marriage and Family
Mental and Emotional Health
Moral and Ethical Decision-Making
Overcoming Temptation and Sin
Personal Faith and Relationship with God
Prayer and Spiritual Disciplines
Relationships and Community
Sexual Purity and Enjoyment
Spiritual Warfare
Stewardship of the Body
Time Management and Priorities
Understanding the Bible
Work and Stewardship

9. Concept: Key theological or practical concept
10. Difficulty Level: Number 1-5 (1=foundational, 5=deep/complex)
11. Related Verses: 2-4 comma-separated related verse references
12. Contextual Meaning: 2-3 sentences explaining practical application without quotes
13. Theme Analysis (values between 0-1):
- Relational Impact: 0-1
- Missional Impact: 0-1
- Emotional Impact: 0-1
- Physical Impact: 0-1
- Spiritual Impact: 0-1
- Vocational Impact: 0-1

Requirements:
- Ensure even distribution across all Growth Areas
- Include at least one verse for each Development Topic
- Vary difficulty levels (include all levels 1-5)
- Format each entry as a clear list with labeled fields
- Do not use any quotation marks in the output
- Ensure URLs are for NIV version on Bible.com
- Make reflection questions practical and applicable
- Keep contextual meanings clear and actionable
- Ensure theme impact values reflect the verse's focus

Please provide 25 complete verse entries, numbered 1-25, with all fields properly formatted for direct use in a spreadsheet.`}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Choose File
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".txt,.tsv"
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <button
                  onClick={handleUpload}
                  disabled={!file}
                  className={`w-full py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    file
                      ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Upload File
                </button>

                {uploadMessage && (
                  <p className="mt-4 text-sm text-center text-gray-600">
                    {uploadMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>

      {/* Persistent Upload Result Modal */}
      {uploadResult.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{uploadResult.message}</h3>
            
            {uploadResult.details && (
              <div className="mb-4 max-h-60 overflow-y-auto">
                {typeof uploadResult.details === 'object' ? (
                  <pre className="whitespace-pre-wrap text-sm">
                    {JSON.stringify(uploadResult.details, null, 2)}
                  </pre>
                ) : (
                  <p className="text-sm">{uploadResult.details}</p>
                )}
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setUploadResult({ show: false, message: '', details: null })}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Normalization Confirmation Modal */}
      {normalizeConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full text-center">
            <h2 className="text-xl font-bold mb-4">Reference Normalization</h2>
            <p className="mb-2">The following references were automatically normalized:</p>
            <ul className="mb-4 text-left max-h-48 overflow-y-auto">
              {normalizeConfirm.changes.map(([orig, norm], idx) => (
                <li key={idx} className="mb-1 text-sm">
                  <span className="font-semibold">{orig}</span> → <span className="text-blue-700 font-semibold">{norm}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => setNormalizeConfirm({ show: false, changes: [] })}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadVerses;
